/** An equipment group CODE (editable per institution, see EquipmentGroupDTO). */
export type EquipmentCategory = string;
export type EquipmentStatus = 'RUNNING' | 'IDLE' | 'DOWN' | 'UNDER_MAINTENANCE' | 'RETIRED';
export type EquipmentOwnership = 'OWNED' | 'RENTED' | 'BORROWED' | 'LEASED';
export type Criticality = 'A' | 'B' | 'C';
export type MeterKind = 'CUMULATIVE' | 'GAUGE' | 'QUANTITY';
export type FailureCodeKind = 'SYMPTOM' | 'CAUSE' | 'ACTION';

export const EQUIPMENT_STATUSES: EquipmentStatus[] = ['RUNNING', 'IDLE', 'DOWN', 'UNDER_MAINTENANCE', 'RETIRED'];
export const EQUIPMENT_OWNERSHIPS: EquipmentOwnership[] = ['OWNED', 'RENTED', 'BORROWED', 'LEASED'];
export const CRITICALITIES: Criticality[] = ['A', 'B', 'C'];
export const METER_KINDS: MeterKind[] = ['CUMULATIVE', 'GAUGE', 'QUANTITY'];
export const FAILURE_CODE_KINDS: FailureCodeKind[] = ['SYMPTOM', 'CAUSE', 'ACTION'];

/** "PRODUCTION_MACHINE" -> "Production machine". */
export const mntLabel = (v?: string | null): string =>
    v ? v.charAt(0) + v.slice(1).toLowerCase().replace(/_/g, ' ') : '';

export interface EquipmentGroupDTO {
    id: string; institutionId: string; code: string; name: string; sortOrder: number;
    isSystem: boolean; active: boolean; notes?: string | null; usedBy: number;
}
export interface EquipmentGroupRequest {
    institutionId: string; code: string; name: string; sortOrder?: number | null; active?: boolean; notes?: string | null;
}

export interface MeterDTO {
    id: string;
    equipmentId: string;
    code: string;
    name: string;
    kind: MeterKind;
    unit?: string | null;
    minValue?: number | null;
    maxValue?: number | null;
    isPmDriver: boolean;
    active: boolean;
    lastValue?: number | null;
    lastReadAt?: string | null;
}

export interface MeterRequest {
    code: string;
    name: string;
    kind: MeterKind;
    unit?: string | null;
    minValue?: number | null;
    maxValue?: number | null;
    isPmDriver?: boolean;
    active?: boolean;
}

export interface EquipmentDTO {
    id: string;
    institutionId: string;
    code: string;
    name: string;
    equipmentTypeId: string;
    equipmentTypeName?: string | null;
    category: EquipmentCategory;
    manufacturer?: string | null;
    model?: string | null;
    serialNo?: string | null;
    status: EquipmentStatus;
    criticality: Criticality;
    ownership: EquipmentOwnership;
    ownerParty?: string | null;
    rentalUntil?: string | null;
    locationDepartmentId?: string | null;
    locationDepartmentName?: string | null;
    parentEquipmentId?: string | null;
    parentEquipmentCode?: string | null;
    fixedAssetId?: string | null;
    fixedAssetCode?: string | null;
    commissionedOn?: string | null;
    warrantyUntil?: string | null;
    attributes?: Record<string, unknown> | string | null;
    retiredOn?: string | null;
    retiredReason?: string | null;
    notes?: string | null;
    meters?: MeterDTO[];
}

export interface EquipmentRequest {
    institutionId: string;
    code: string;
    name: string;
    equipmentTypeId: string;
    manufacturer?: string | null;
    model?: string | null;
    serialNo?: string | null;
    criticality?: Criticality;
    ownership?: EquipmentOwnership;
    ownerParty?: string | null;
    rentalUntil?: string | null;
    locationDepartmentId?: string | null;
    parentEquipmentId?: string | null;
    fixedAssetId?: string | null;
    commissionedOn?: string | null;
    warrantyUntil?: string | null;
    attributes?: Record<string, unknown> | null;
    notes?: string | null;
}

export interface EquipmentMoveDTO {
    id: string;
    equipmentId: string;
    fromDepartmentId?: string | null;
    fromDepartmentName?: string | null;
    toDepartmentId?: string | null;
    toDepartmentName?: string | null;
    movedAt: string;
    movedBy?: string | null;
    reason?: string | null;
}

export interface EquipmentFilters {
    q?: string;
    typeId?: string;
    category?: EquipmentCategory | '';
    status?: EquipmentStatus | '';
    departmentId?: string;
}

export interface EquipmentTypeMeterDTO {
    id?: string;
    code: string;
    name: string;
    kind: MeterKind;
    unit?: string | null;
    minValue?: number | null;
    maxValue?: number | null;
    isPmDriver?: boolean;
    sortOrder?: number;
}

export interface EquipmentTypeDTO {
    id: string;
    institutionId: string;
    code: string;
    name: string;
    category: EquipmentCategory;
    isSystem: boolean;
    active: boolean;
    notes?: string | null;
    meters: EquipmentTypeMeterDTO[];
}

export interface EquipmentTypeRequest {
    institutionId: string;
    code: string;
    name: string;
    category: EquipmentCategory;
    active?: boolean;
    notes?: string | null;
    meters?: EquipmentTypeMeterDTO[] | null;
}

export interface FailureCodeDTO {
    id: string;
    institutionId: string;
    kind: FailureCodeKind;
    code: string;
    name: string;
    equipmentCategory?: EquipmentCategory | null;
    active: boolean;
}

export interface FailureCodeRequest {
    institutionId: string;
    kind: FailureCodeKind;
    code: string;
    name: string;
    equipmentCategory?: EquipmentCategory | null;
    active?: boolean;
}

export interface MaintenanceSettings {
    institutionId?: string;
    maintenanceStoreId?: string | null;
    requestDuplicateWindowMinutes: number;
    archiveClosedAfterDays: number;
    pmGenerationHorizonDays: number;
    requireFailureCodeOnClose: boolean;
}

export interface SeedResult {
    equipmentTypesCreated: number;
    meterTemplatesCreated: number;
    failureCodesCreated: number;
}

export interface EquipmentImportRow {
    row: number;
    code: string;
    ok: boolean;
    message: string;
}

export interface EquipmentImportResult {
    dryRun: boolean;
    totalRows: number;
    validRows: number;
    createdRows: number;
    rows: EquipmentImportRow[];
}

export interface MntPage<T> {
    content: T[];
    totalElements: number;
    number?: number;
    size?: number;
}

// ---- L2: meter readings and utility log sheet ----
export type ReadingSource = 'MANUAL' | 'IMPORT' | 'WORK_ORDER';

export interface MeterReadingDTO {
    id: string;
    meterId: string;
    readAt: string;
    readingDate: string;
    shiftLabel?: string | null;
    value: number;
    delta?: number | null;
    isReset: boolean;
    outOfRange: boolean;
    source: ReadingSource;
    recordedBy?: string | null;
    recordedByName?: string | null;
    note?: string | null;
    workOrderId?: string | null;
    correctsReadingId?: string | null;
}

export interface LogSheetRow {
    meterId: string;
    equipmentId: string;
    equipmentCode: string;
    equipmentName: string;
    equipmentStatus: EquipmentStatus;
    category: EquipmentCategory;
    locationName?: string | null;
    meterCode: string;
    meterName: string;
    kind: MeterKind;
    unit?: string | null;
    minValue?: number | null;
    maxValue?: number | null;
    lastValue?: number | null;
    lastReadAt?: string | null;
    readings: MeterReadingDTO[];
}

export interface LogSheet {
    date: string;
    rows: LogSheetRow[];
    truncated: boolean;
}

export interface MeterReadingItem {
    meterId: string;
    value: number;
    readAt?: string;
    shiftLabel?: string;
    isReset?: boolean;
    note?: string;
}

export interface MeterReadingBatchResult {
    saved: number;
    failed: number;
    items: { meterId: string; ok: boolean; message?: string | null; reading?: MeterReadingDTO | null }[];
}

export interface MeterSummary {
    meter: MeterDTO;
    institutionId: string;
    equipmentCode: string;
    equipmentName: string;
    locationName?: string | null;
}
// ---------------------------------------------------------------- L1: requests + work orders

export const REQUEST_STATUSES = ['SUBMITTED', 'ACCEPTED', 'REJECTED', 'MERGED', 'CANCELLED', 'RESOLVED', 'CLOSED'] as const;
export type RequestStatus = typeof REQUEST_STATUSES[number];
export const WORK_ORDER_TYPES = ['BREAKDOWN', 'PREVENTIVE', 'CORRECTIVE', 'INSPECTION'] as const;
export type WorkOrderType = typeof WORK_ORDER_TYPES[number];
export const WORK_ORDER_STATUSES = ['OPEN', 'ASSIGNED', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED', 'CLOSED', 'CANCELLED'] as const;
export type WorkOrderStatus = typeof WORK_ORDER_STATUSES[number];
export const WORK_ORDER_PRIORITIES = ['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const;
export type WorkOrderPriority = typeof WORK_ORDER_PRIORITIES[number];
export const HOLD_REASONS = ['WAITING_PARTS', 'WAITING_VENDOR', 'WAITING_PRODUCTION', 'OTHER'] as const;
export type HoldReason = typeof HOLD_REASONS[number];
export const CHECKLIST_RESULTS = ['OK', 'NOT_OK', 'NA'] as const;
export type ChecklistResult = typeof CHECKLIST_RESULTS[number];

export interface WorkOrderDTO {
    id: string;
    version?: number;
    woNo: string;
    institutionId: string;
    type: WorkOrderType;
    priority: WorkOrderPriority;
    status: WorkOrderStatus;
    equipmentId?: string | null;
    equipmentCode?: string | null;
    equipmentName?: string | null;
    locationDepartmentId?: string | null;
    locationName?: string | null;
    breakdownRequestId?: string | null;
    pmPlanId?: string | null;
    pmDueDate?: string | null;
    title: string;
    description?: string | null;
    assignedTo?: string | null;
    assignedToName?: string | null;
    assignedAt?: string | null;
    startedAt?: string | null;
    completedAt?: string | null;
    closedAt?: string | null;
    holdReason?: HoldReason | null;
    holdNote?: string | null;
    machineDown?: boolean;
    downFrom?: string | null;
    backInServiceAt?: string | null;
    downtimeMinutes?: number | null;
    symptomCodeId?: string | null;
    causeCodeId?: string | null;
    actionCodeId?: string | null;
    workDone?: string | null;
    externalVendorName?: string | null;
    cancelReason?: string | null;
    createdAt?: string;
    /** Set when the nightly archive job (or nobody, after a restore) moved this order out of the default list. */
    archivedAt?: string | null;
    /** Manager shape only: absent for technicians. */
    externalSupplierId?: string | null;
    externalCostMemo?: number | null;
    externalBillRef?: string | null;
    supplierBillId?: string | null;
    partsValue?: number | null;
}

export interface LabourDTO {
    id: string;
    technicianId: string;
    technicianName?: string | null;
    startedAt?: string | null;
    endedAt?: string | null;
    minutes?: number | null;
    note?: string | null;
}

export interface ChecklistItemDTO {
    id: string;
    sortOrder: number;
    task: string;
    result?: ChecklistResult | null;
    remark?: string | null;
    doneBy?: string | null;
    doneAt?: string | null;
}

export interface PartLineDTO {
    itemId: string;
    itemName?: string | null;
    itemCode?: string | null;
    requiredQty: number;
    approvedQty?: number | null;
    issued?: number | null;
}

export interface WoRequisitionView {
    id: string;
    requisitionId: string;
    refNo?: string | null;
    status?: string | null;
    remarks?: string | null;
    linkMode: 'CREATED' | 'ATTACHED';
    linkedAt?: string | null;
    lines: PartLineDTO[];
    /** Manager shape only. */
    issuedValue?: number | null;
    /** The caller raised this requisition, so its page opens for them without the store keys. */
    raisedByMe?: boolean;
}

export interface WorkOrderDetailDTO {
    order: WorkOrderDTO;
    labour: LabourDTO[];
    checklist: ChecklistItemDTO[];
    parts: WoRequisitionView[];
    canSeeValues: boolean;
    meters: WoMeterDTO[];
}

export interface WoMeterDTO {
    id: string;
    code: string;
    name: string;
    unit: string;
    lastValue?: number | null;
    lastReadAt?: string | null;
}

export interface WorkOrderMeterReading {
    meterId: string;
    value: number;
    readAt?: string;
}

export interface TechnicianDTO {
    userId: string;
    name: string;
}

export interface WorkOrderFilters {
    status?: string;
    type?: string;
    assignee?: string;
    equipmentId?: string;
    departmentId?: string;
    overdue?: boolean;
    archived?: boolean;
}

export interface WorkOrderCreateRequest {
    institutionId: string;
    type: WorkOrderType;
    priority?: WorkOrderPriority;
    equipmentId?: string;
    locationDepartmentId?: string;
    title: string;
    description?: string;
    assignedTo?: string;
}

export interface WorkOrderCompleteRequest {
    workDone: string;
    backInServiceAt?: string;
    stillDown?: boolean;
    symptomCodeId?: string;
    causeCodeId?: string;
    actionCodeId?: string;
    meterReading?: WorkOrderMeterReading;
}

export interface WorkOrderCloseRequest {
    causeCodeId?: string;
    actionCodeId?: string;
}

export interface LabourRequest {
    technicianId?: string;
    startedAt?: string;
    endedAt?: string;
    minutes?: number;
    note?: string;
}

export interface ExternalRepairRequest {
    vendorName?: string;
    supplierId?: string;
    costMemo?: number;
    billRef?: string;
    supplierBillId?: string;
}

export interface BreakdownRequestDTO {
    id: string;
    requestNo: string;
    institutionId: string;
    requestedBy?: string;
    requestedByName?: string | null;
    equipmentId?: string | null;
    equipmentCode?: string | null;
    equipmentName?: string | null;
    equipmentCriticality?: string | null;
    locationDepartmentId?: string | null;
    locationName?: string | null;
    machineStopped: boolean;
    stoppedAt?: string | null;
    description: string;
    symptomCodeId?: string | null;
    status: RequestStatus;
    workOrderId?: string | null;
    mergedIntoRequestId?: string | null;
    rejectionReason?: string | null;
    possibleDuplicateOf?: string | null;
    createdAt?: string;
    resolvedAt?: string | null;
    closedAt?: string | null;
}

export interface MyBreakdownRequestView {
    id: string;
    requestNo: string;
    institutionId: string;
    equipmentId?: string | null;
    equipmentCode?: string | null;
    equipmentName?: string | null;
    locationDepartmentId?: string | null;
    locationName?: string | null;
    machineStopped: boolean;
    stoppedAt?: string | null;
    description: string;
    status: RequestStatus;
    workOrderNo?: string | null;
    workOrderStatus?: WorkOrderStatus | null;
    technicianName?: string | null;
    rejectionReason?: string | null;
    createdAt?: string;
    resolvedAt?: string | null;
}

export interface RaiseBreakdownRequest {
    institutionId: string;
    equipmentId?: string;
    locationDepartmentId?: string;
    machineStopped?: boolean;
    stoppedAt?: string;
    description: string;
    symptomCodeId?: string;
}

export interface EquipmentLookupDTO {
    id: string;
    code: string;
    name: string;
    locationName?: string | null;
}

export interface LocationLookupDTO {
    id: string;
    name: string;
    unitType?: string | null;
    parentId?: string | null;
}

// ---------------------------------------------------------------- L3: preventive maintenance
export const PM_TRIGGERS = ['CALENDAR', 'METER', 'CALENDAR_OR_METER'] as const;
export type PmTrigger = typeof PM_TRIGGERS[number];
export const PM_BASES = ['FIXED', 'FLOATING'] as const;
export type PmBasis = typeof PM_BASES[number];
export const CALENDAR_UNITS = ['DAY', 'WEEK', 'MONTH'] as const;
export type CalendarUnit = typeof CALENDAR_UNITS[number];
export type PmState = 'OVERDUE' | 'DUE_SOON' | 'OK' | 'INACTIVE';

export interface PmPlanDTO {
    id: string;
    institutionId: string;
    equipmentId: string;
    equipmentCode: string;
    equipmentName: string;
    locationName?: string | null;
    name: string;
    trigger: PmTrigger;
    basis: PmBasis;
    everyCount?: number | null;
    everyUnit?: CalendarUnit | null;
    meterId?: string | null;
    meterCode?: string | null;
    meterUnit?: string | null;
    meterLastValue?: number | null;
    everyMeter?: number | null;
    leadDays: number;
    leadMeter?: number | null;
    startDate: string;
    nextDueDate?: string | null;
    nextDueMeter?: number | null;
    lastDoneDate?: string | null;
    lastDoneMeter?: number | null;
    defaultAssignee?: string | null;
    defaultAssigneeName?: string | null;
    priority: WorkOrderPriority;
    estimatedMinutes?: number | null;
    active: boolean;
    batchKey?: string | null;
    state: PmState;
    daysUntilDue?: number | null;
    meterRemaining?: number | null;
    openWorkOrderId?: string | null;
    openWorkOrderNo?: string | null;
    openWorkOrderStatus?: WorkOrderStatus | null;
    tasks?: string[] | null;
    createdAt?: string;
}

/** Fields shared by create, update and the bulk template. */
export interface PmPlanFields {
    name: string;
    trigger: PmTrigger;
    basis?: PmBasis;
    everyCount?: number | null;
    everyUnit?: CalendarUnit | null;
    everyMeter?: number | null;
    leadDays?: number | null;
    leadMeter?: number | null;
    startDate: string;
    defaultAssignee?: string | null;
    priority?: WorkOrderPriority;
    estimatedMinutes?: number | null;
    tasks?: string[];
}

export interface PmPlanCreateRequest extends PmPlanFields {
    institutionId: string;
    equipmentId: string;
    meterId?: string | null;
}

export interface PmPlanUpdateRequest extends PmPlanFields {
    meterId?: string | null;
    active?: boolean;
}

export interface PmBulkRequest {
    template: PmPlanFields & { meterCode?: string | null };
    equipmentFilter?: { equipmentTypeId?: string | null; locationDepartmentId?: string | null };
    spreadDays?: number | null;
    preview?: boolean;
}

export interface PmBulkResult { preview: boolean; matched: number; created: number; skipped: number; batchKey?: string | null }
export interface PmBatchEditRequest {
    active?: boolean; priority?: WorkOrderPriority; leadDays?: number; defaultAssignee?: string; tasks?: string[];
}
export interface PmGenerationResult { checked: number; created: number; skipped: number; failed: number; workOrderIds: string[] }

// ---- Maintenance L4: dashboards (no money field anywhere in these shapes) ----
export interface MntListBlock<T> { total: number; items: T[]; }
export interface MntChartPoint { label: string; value: number; }
export interface MntDayPoint { date: string; opened: number; closed: number; }
export interface MntMonthCompliance { month: string; due: number; onTime: number; percent: number | null; }
export interface MntManagerKpis {
    machinesDownNow: number; openBreakdowns: number; newRequests: number; pmOverdue: number; pmDue7Days: number;
    downtimeTodayMinutes: number; downtimeMtdHours: number; mttrMtdMinutes: number | null;
}
export interface MntDashRequestRow {
    id: string; requestNo: string; equipmentCode?: string | null; equipmentName?: string | null;
    locationName?: string | null; machineStopped: boolean; description: string; possibleDuplicate: boolean;
    createdAt?: string | null;
}
export interface MntDashWoRow {
    id: string; woNo: string; type: string; priority: string; status: string; title: string;
    equipmentCode?: string | null; equipmentName?: string | null; locationName?: string | null;
    machineDown: boolean; pmDueDate?: string | null; assignedToName?: string | null;
    createdAt?: string | null; completedAt?: string | null;
}
export interface MntPartsWaitRow {
    id: string; woNo: string; title: string; equipmentCode?: string | null; equipmentName?: string | null;
    requisitionRefNo?: string | null; requisitionStatus?: string | null; createdAt?: string | null;
}
export interface MntDashPmRow {
    planId: string; planName: string; equipmentCode?: string | null; equipmentName?: string | null;
    locationName?: string | null; nextDueDate?: string | null; daysUntilDue?: number | null; state: string;
    defaultAssigneeName?: string | null;
}
export interface MntDashReadingRow {
    meterId: string; equipmentId: string; equipmentCode?: string | null; equipmentName?: string | null;
    locationName?: string | null; meterCode?: string | null; meterName?: string | null; unit?: string | null;
}
export interface MntManagerDashboard {
    kpis: MntManagerKpis;
    downtimeByLine30d: MntChartPoint[];
    breakdownsByCauseMtd: MntChartPoint[];
    workOrdersOpenedVsClosed30d: MntDayPoint[];
    pmCompliance6m: MntMonthCompliance[];
    newRequests: MntListBlock<MntDashRequestRow>;
    unassignedWorkOrders: MntListBlock<MntDashWoRow>;
    waitingParts: MntListBlock<MntPartsWaitRow>;
    overduePm: MntListBlock<MntDashPmRow>;
    awaitingClose: MntListBlock<MntDashWoRow>;
    readingsMissingToday: MntListBlock<MntDashReadingRow>;
}
export interface MntTechnicianKpis {
    myOpen: number; inProgress: number; waitingParts: number; completedToday: number; readingsDueToday: number;
}
export interface MntTechWoRow {
    id: string; woNo: string; type: string; priority: string; status: string; title: string;
    equipmentCode?: string | null; equipmentName?: string | null; locationName?: string | null;
    machineDown: boolean; pmDueDate?: string | null; createdAt?: string | null; completedAt?: string | null;
}
export interface MntTechnicianDashboard {
    kpis: MntTechnicianKpis;
    myWork: MntListBlock<MntTechWoRow>;
    pmDueMine: MntListBlock<MntTechWoRow>;
    readingsToRecord: MntListBlock<MntDashReadingRow>;
    recentCompletions: MntListBlock<MntTechWoRow>;
}

// ---- L5: reports (REPORT_VIEW). Money fields exist only on these report rows. ----
export interface BreakdownLogRow {
    workOrderId: string; woNo: string; status: string; equipmentCode?: string | null; equipmentName?: string | null;
    line?: string | null; reportedAt?: string | null; stoppedAt?: string | null; backInServiceAt?: string | null;
    downtimeMinutes?: number | null; symptom?: string | null; cause?: string | null; action?: string | null;
    technician?: string | null; partsSummary?: string | null; partsValue?: number | null; externalCostMemo?: number | null;
}
export interface DowntimeRow { group: string; period: string; stoppages: number; minutes: number }
export interface MttrMtbfRow {
    id: string; code?: string | null; name?: string | null; type?: string | null; breakdowns: number;
    downtimeMinutes: number; mttrMinutes?: number | null; mtbfHours?: number | null; insufficientData: boolean;
}
export interface ProblemMachineRow {
    rank: number; equipmentId: string; code: string; name: string; type?: string | null; line?: string | null;
    breakdowns: number; downtimeMinutes: number;
}
export interface PmComplianceRow {
    key: string; label: string; due: number; onTime: number; late: number; openOverdue: number; upcoming: number;
    onTimePercent?: number | null;
}
export interface BacklogRow {
    status: string; upTo2Days: number; days3To7: number; days8To14: number; days15To30: number; over30Days: number; total: number;
}
export interface PartsUsedRow { month: string; id: string; code?: string | null; name?: string | null; qty: number; value: number }
export interface UnlinkedIssueRow {
    requisitionId: string; refNo?: string | null; remarks?: string | null; lastIssuedAt?: string | null; qty: number; requisitionValue: number;
}
export interface UtilityRow {
    meterId: string; equipmentCode?: string | null; equipmentName?: string | null; meterCode?: string | null;
    meterName?: string | null; kind: MeterKind; unit?: string | null; period: string; total?: number | null;
    min?: number | null; avg?: number | null; max?: number | null; readings: number;
}
export interface OutOfRangeRow {
    readingId: string; meterId: string; date: string; readAt?: string | null; equipmentCode?: string | null;
    equipmentName?: string | null; meterName?: string | null; unit?: string | null; value: number;
    minValue?: number | null; maxValue?: number | null; note?: string | null;
}
export interface GeneratorRow {
    equipmentId: string; code: string; name: string; runHours?: number | null; kwh?: number | null;
    fuelMeter?: string | null; fuelUnit?: string | null; fuelUsed?: number | null; fuelPerKwh?: number | null;
    kwhPerRunHour?: number | null;
}
export interface RegisterRow {
    id: string; code: string; name: string; type?: string | null; category: EquipmentCategory;
    manufacturer?: string | null; model?: string | null; serialNo?: string | null; status: string;
    criticality?: string | null; ownership: string; ownerParty?: string | null; rentalUntil?: string | null;
    location?: string | null; fixedAsset?: string | null; commissionedOn?: string | null; warrantyUntil?: string | null;
    retiredOn?: string | null; idle: boolean;
}

// ---- repair bills ----
export type RepairBillStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED';
export interface RepairBillDTO {
    id: string; institutionId: string; workOrderId: string; woNo: string; woTitle?: string | null;
    equipmentId?: string | null; equipmentCode?: string | null; equipmentName?: string | null;
    fixedAssetId?: string | null; fixedAssetCode?: string | null; fixedAssetName?: string | null;
    supplierId?: string | null; supplierName?: string | null; vendorName?: string | null;
    amount: number; billRef?: string | null; billDate?: string | null; note?: string | null;
    status: RepairBillStatus; submittedBy?: string | null; submittedByName?: string | null; submittedAt?: string | null;
    decidedBy?: string | null; decidedByName?: string | null; decidedAt?: string | null; rejectReason?: string | null;
    supplierBillId?: string | null; attachmentCount: number; createdAt?: string | null;
    /** Set only on approve / link, e.g. the bill total differs from the repair bill amount. */
    warning?: string | null;
}
export interface RepairBillSaveRequest {
    supplierId?: string | null; vendorName?: string | null; amount: number;
    billRef?: string | null; billDate?: string | null; note?: string | null;
}
export interface RepairBillFile { id: string; fileName: string; contentType?: string | null; sizeBytes: number; createdAt?: string | null }
